import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { PainelAgente } from '../componentes/PainelAgente.tsx';
import { api, query } from '../lib/api.ts';
import { dataCurta, diasAtras, hojeIso } from '../lib/formato.ts';
import { usePode, useUsuario } from '../lib/sessao.tsx';
import './inicio.css';

/* Onde o administrador começa o dia.
 *
 * Quatro números e um botão. A recepção não passa por aqui -- ela cai direto na
 * fila, que é onde trabalha o dia inteiro; quem precisa de visão de conjunto é
 * quem responde pela clínica.
 *
 * Cada número leva à lista que ele conta, com o mesmo filtro e o mesmo período.
 * Contador que não abre nada é enfeite, e contador que abre uma lista diferente
 * da que mostrou é pior: faz duvidar dos dois. */

const PERIODOS = [
  { chave: '7', rotulo: '7 dias', dias: 7 },
  { chave: '30', rotulo: '30 dias', dias: 30 },
  { chave: '1', rotulo: 'Hoje', dias: 0 },
] as const;

/* A ordem é a do fluxo do exame, não a da importância: quem lê a linha entende
   o caminho que um exame percorre só de olhar. */
/* A cor de cada cartão é a MESMA do selo daquele estágio na fila. Se o painel
   pintasse "aguardando laudo" de um jeito e a lista de outro, seriam dois
   vocabulários de cor para a mesma coisa, e a cor deixaria de informar. */
const CARTOES: { situacao: string; rotulo: string; apoio: string; destaque?: boolean }[] = [
  {
    situacao: 'AGUARDANDO_EXAME',
    rotulo: 'Aguardando exame',
    apoio: 'cadastrados, sem imagem ainda',
  },
  {
    situacao: 'AGUARDANDO_LAUDO',
    rotulo: 'Aguardando laudo',
    apoio: 'imagem no sistema, esperando o médico',
  },
  { situacao: 'RECUSADO', rotulo: 'Recusados', apoio: 'o médico devolveu', destaque: true },
  { situacao: 'FINALIZADO', rotulo: 'Finalizados', apoio: 'encerrados no período' },
];

/* Os atalhos de cadastro. A cor separa o que é ação do dia (atender, lançar
   exame) do que é cadastro de apoio, mexido de vez em quando -- sem isso são
   oito retângulos iguais e a pessoa lê os oito toda vez para achar um. */
const ATALHOS: { para: string; nome: string; cor: string; permissoes: Parameters<ReturnType<typeof usePode>> }[] = [
  { para: '/atendimento', nome: 'Novo atendimento', cor: 'acao', permissoes: ['pode_exame_cadastrar'] },
  { para: '/exames/novo', nome: 'Novo exame', cor: 'exame', permissoes: ['pode_exame_cadastrar'] },
  { para: '/pacientes', nome: 'Pacientes', cor: 'paciente', permissoes: ['pode_pacientes', 'pode_exame_cadastrar'] },
  { para: '/empresas', nome: 'Empresas', cor: 'empresa', permissoes: ['pode_empresas'] },
  { para: '/procedimentos', nome: 'Procedimentos', cor: 'apoio', permissoes: ['pode_procedimentos'] },
  { para: '/convenios', nome: 'Convênios', cor: 'apoio', permissoes: ['pode_convenios'] },
  { para: '/medicos', nome: 'Médicos', cor: 'apoio', permissoes: ['pode_medicos'] },
  { para: '/usuarios', nome: 'Usuários', cor: 'apoio', permissoes: ['pode_usuarios'] },
];

export function Inicio() {
  const usuario = useUsuario();
  const pode = usePode();
  const [dias, setDias] = useState(7);

  const de = dias === 0 ? hojeIso() : diasAtras(dias);
  const ate = hojeIso();

  /* Reaproveita a rota da fila, pedindo a menor página possível: o que
     interessa aqui é o `resumo`, que já vem com a contagem por situação. Uma
     rota nova só para contar seria outro lugar para a conta divergir. */
  const dados = useQuery({
    queryKey: ['inicio', de, ate],
    queryFn: ({ signal }) =>
      api.get<{ total: number; resumo: Record<string, number> }>(
        `/exames${query({ de, ate, porPagina: 10, situacao: 'TODAS' })}`,
        signal,
      ),
    staleTime: 30_000,
  });

  const resumo = dados.data?.resumo ?? {};
  const periodo = `${dataCurta(de)} a ${dataCurta(ate)}`;

  return (
    <Layout>
      <Cabecalho titulo={`Olá, ${usuario.nome.split(' ')[0]}`} />

      <div className="inicio__periodo">
        <span className="rotulo">Período</span>
        <div className="inicio__abas" role="group" aria-label="Período dos números">
          {PERIODOS.map((p) => (
            <button
              key={p.chave}
              type="button"
              className="inicio__aba"
              data-ativa={dias === p.dias || undefined}
              onClick={() => setDias(p.dias)}
            >
              {p.rotulo}
            </button>
          ))}
        </div>
        <span className="inicio__intervalo">{periodo}</span>
      </div>

      <div className="inicio__cartoes">
        {CARTOES.map((c) => {
          const n = resumo[c.situacao] ?? 0;
          return (
            <Link
              key={c.situacao}
              className="cartao inicio__cartao"
              data-situacao={c.situacao}
              data-alerta={c.destaque && n > 0 ? '' : undefined}
              to={`/fila${query({ situacao: c.situacao, de, ate })}`}
            >
              <span className="inicio__numero num">{dados.isPending ? '—' : n}</span>
              <span className="inicio__rotulo">{c.rotulo}</span>
              <span className="inicio__apoio">{c.apoio}</span>
            </Link>
          );
        })}
      </div>

      <PainelAgente />

      <section className="cartao inicio__atalhos">
        <h2 className="painel__titulo">Atalhos</h2>
        <div className="inicio__links">
          {ATALHOS.filter((a) => pode(...a.permissoes)).map((a) => (
            <Link key={a.para} to={a.para} data-cor={a.cor}>
              {a.nome}
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
